import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import MemoryGame from '../components/MemoryGame';
import TicTacToe from '../components/TicTacToe';
import VirtualPhotoBooth from '../components/VirtualPhotoBooth';
import Quiz from './Quiz';

interface Game {
  id: string;
  name: string;
  description: string;
  icon: string;
  color: string;
  component: React.ComponentType<any>;
}

const InteractiveGames: React.FC = () => {
  const [activeGame, setActiveGame] = useState<string | null>(null);
  const [gameStats, setGameStats] = useState(() => {
    const savedStats = localStorage.getItem('gameStats');
    return savedStats ? JSON.parse(savedStats) : {
      memoryGame: { bestScore: 0, gamesPlayed: 0 },
      quiz: { bestScore: 0, gamesPlayed: 0, bestStreak: 0 },
      ticTacToe: { gamesPlayed: 0, wins: 0, draws: 0 },
      photoBooth: { photos: 0 }
    };
  });

  const games: Game[] = [
    {
      id: 'memory',
      name: 'Memory Match',
      description: 'Match couple photos in this fun memory game!',
      icon: '🧠',
      color: 'from-blue-500 to-indigo-600',
      component: MemoryGame
    },
    {
      id: 'quiz',
      name: 'Love Quiz',
      description: 'Test how well you know Noor & Priya!',
      icon: '🎯',
      color: 'from-yellow-500 to-orange-600',
      component: Quiz
    },
    {
      id: 'ticTacToe',
      name: 'Wedding Tic-Tac-Toe',
      description: 'Play the classic game with hearts vs rings!',
      icon: '💕',
      color: 'from-pink-500 to-rose-600',
      component: TicTacToe
    },
    {
      id: 'booth',
      name: 'Photo Booth',
      description: 'Take fun photos with virtual filters!',
      icon: '📸',
      color: 'from-purple-500 to-violet-600',
      component: VirtualPhotoBooth
    }
  ];

  // Load game stats from localStorage
  useEffect(() => {
    const savedStats = localStorage.getItem('gameStats');
    if (savedStats) {
      setGameStats(JSON.parse(savedStats));
    }
  }, []);

  // Save game stats to localStorage
  const updateGameStats = (gameId: string, newStats: any) => {
    setGameStats((prev: any) => {
      const updated = { ...prev, [gameId]: { ...prev[gameId as keyof typeof prev], ...newStats } };
      localStorage.setItem('gameStats', JSON.stringify(updated));
      return updated;
    });
  };

  const renderActiveGame = () => {
    const game = games.find(g => g.id === activeGame);
    if (!game || !activeGame) return null;

    const GameComponent = game.component;
    return (
      <motion.div
        key={activeGame}
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: -20 }}
        transition={{ duration: 0.3 }}
        className="w-full"
      >
        <div className="mb-6 flex items-center justify-between">
          <button
            onClick={() => setActiveGame(null)}
            className="flex items-center space-x-2 text-slate-600 hover:text-slate-800 transition-colors"
          >
            <span className="text-2xl">←</span>
            <span className="font-medium">Back to Games</span>
          </button>
          <div className="text-right">
            <h2 className="text-2xl font-bold text-slate-800">{game.name}</h2>
            <p className="text-slate-600">{game.description}</p>
          </div>
        </div>
        <GameComponent onStatsUpdate={(stats: any) => updateGameStats(activeGame, stats)} />
      </motion.div>
    );
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-rose-50 to-purple-100 py-20 px-4">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="text-center mb-12"
        >
          <h1 className="text-5xl md:text-6xl font-great-vibes text-slate-800 mt-4 mb-4">
            Interactive Games
          </h1>
          <p className="text-xl text-slate-600 max-w-3xl mx-auto">
            Have fun with these interactive games and activities! 
            <br />Perfect for getting to know Noor & Priya better! 🎉
          </p>
        </motion.div>

        {!activeGame ? (
          <motion.div
            key="games-grid"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
          >
              {/* Games Grid */}
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 mb-12">
                {games.map((game, index) => (
                  <motion.div
                    key={game.id}
                    initial={{ opacity: 0, y: 30 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5, delay: index * 0.1 }}
                    whileHover={{ scale: 1.05, y: -5 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={() => setActiveGame(game.id)}
                    className="group cursor-pointer"
                  >
                    <div className={`bg-gradient-to-br ${game.color} rounded-3xl p-8 text-white shadow-2xl hover:shadow-3xl transition-all duration-300 relative overflow-hidden`}>
                      {/* Background Pattern */}
                      <div className="absolute top-0 right-0 w-24 h-24 opacity-10">
                        <div className="text-4xl">{game.icon}</div>
                      </div>
                      
                      <div className="relative z-10">
                        <div className="text-6xl mb-4">{game.icon}</div>
                        <h3 className="text-2xl font-bold mb-3">{game.name}</h3>
                        <p className="text-white/90 mb-6">{game.description}</p>
                        
                        {/* Game Stats */}
                        <div className="space-y-2 text-sm">
                          {game.id === 'memory' && (
                            <>
                              <div className="flex justify-between">
                                <span>Best Score:</span>
                                <span className="font-bold">{gameStats.memoryGame?.bestScore || 0}</span>
                              </div>
                              <div className="flex justify-between">
                                <span>Games Played:</span>
                                <span className="font-bold">{gameStats.memoryGame?.gamesPlayed || 0}</span>
                              </div>
                            </>
                          )}
                          {game.id === 'ticTacToe' && (
                            <>
                              <div className="flex justify-between">
                                <span>Games Played:</span>
                                <span className="font-bold">{gameStats.ticTacToe?.gamesPlayed || 0}</span>
                              </div>
                              <div className="flex justify-between">
                                <span>Wins:</span>
                                <span className="font-bold">{gameStats.ticTacToe?.wins || 0}</span>
                              </div>
                            </>
                          )}
                          {game.id === 'quiz' && (
                            <>
                              <div className="flex justify-between">
                                <span>Best Score:</span>
                                <span className="font-bold">{gameStats.quiz?.bestScore || 0}</span>
                              </div>
                              <div className="flex justify-between">
                                <span>Games Played:</span>
                                <span className="font-bold">{gameStats.quiz?.gamesPlayed || 0}</span>
                              </div>
                            </>
                          )}
                          {game.id === 'booth' && (
                            <div className="flex justify-between">
                              <span>Photos Taken:</span>
                              <span className="font-bold">{gameStats.photoBooth?.photos || 0}</span>
                            </div>
                          )}
                        </div>
                        
                        <div className="mt-6 text-center">
                          <span className="inline-block bg-white/25 px-6 py-3 rounded-full text-sm font-medium group-hover:bg-white/35 transition-colors duration-300 shadow-lg">
                            Play Now →
                          </span>
                        </div>
                      </div>
                    </div>
                  </motion.div>
                ))}
              </div>

              {/* Quick Stats */}
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.4 }}
                className="bg-white/70 backdrop-blur-sm rounded-3xl p-8 shadow-xl border border-white/50"
              >
                <h3 className="text-2xl font-bold text-slate-800 mb-6 text-center">Your Game Stats</h3>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
                  <div className="text-center">
                    <div className="text-3xl mb-2">🧠</div>
                    <div className="text-2xl font-bold text-blue-600">{gameStats.memoryGame?.bestScore || 0}</div>
                    <div className="text-sm text-slate-600">Best Memory Score</div>
                  </div>
                  <div className="text-center">
                    <div className="text-3xl mb-2">🎯</div>
                    <div className="text-2xl font-bold text-orange-600">{gameStats.quiz?.bestScore || 0}</div>
                    <div className="text-sm text-slate-600">Best Quiz Score</div>
                  </div>
                  <div className="text-center">
                    <div className="text-3xl mb-2">💕</div>
                    <div className="text-2xl font-bold text-pink-600">{gameStats.ticTacToe?.gamesPlayed || 0}</div>
                    <div className="text-sm text-slate-600">Tic-Tac-Toe Games</div>
                  </div>
                  <div className="text-center">
                    <div className="text-3xl mb-2">📸</div>
                    <div className="text-2xl font-bold text-purple-600">{gameStats.photoBooth?.photos || 0}</div>
                    <div className="text-sm text-slate-600">Booth Photos</div>
                  </div>
                </div>
              </motion.div>
            </motion.div>
          ) : (
            renderActiveGame()
          )}
      </div>
    </div>
  );
};

export default InteractiveGames;
